import { memo } from "react";
import { Buildings, FunnelSimple, Wall, X } from "@phosphor-icons/react";

export interface DocumentsScopeBannerProps {
  /** Gebouw waarop gefilterd staat. */
  buildingId?: string;
  /** Renovatieproject waarop gefilterd staat. */
  projectId?: number | string;
  /** Aantal overige actieve filters (jaar, type, status, leverancier, ...). */
  otherFilterCount?: number;
  /** Wanneer meegegeven: knop om de gebouwfilter te wissen. */
  onClearBuilding?: () => void;
}

/**
 * Banner die onmiskenbaar toont **waarop de pagina gefilterd staat**.
 *
 * Stond eerder als een grijze regel onder de paginatitel ("Gefilterd op gebouw
 * 2000-NATI-144"), wat over het hoofd gezien werd — met als gevolg dat de bedragen
 * verkeerd gelezen werden: je zag een gefilterd bedrag en dacht dat het het totaal was.
 *
 * Zonder filter wordt er niets getoond: dan is er ook niets om te verduidelijken, en een
 * permanente "geen filter"-balk is enkel ruis.
 */
export const DocumentsScopeBanner = memo(
  ({
    buildingId,
    projectId,
    otherFilterCount = 0,
    onClearBuilding,
  }: DocumentsScopeBannerProps) => {
    if (!buildingId && !projectId && otherFilterCount === 0) return null;

    return (
      <div className="px-6 pb-1">
        <div className="border-moss-primary/40 bg-moss-primary/10 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2">
          <FunnelSimple size={15} weight="duotone" className="text-moss-primary shrink-0" />
          <span className="text-neutral-primary text-sm font-semibold">
            Gefilterde weergave
          </span>

          {buildingId && (
            <span className="border-moss-primary/30 text-moss-primary inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-xs font-medium">
              <Buildings size={11} weight="duotone" />
              Gebouw {buildingId}
              {onClearBuilding && (
                <button
                  type="button"
                  onClick={onClearBuilding}
                  title="Gebouwfilter wissen"
                  className="hover:text-moss-primary/70 ml-0.5"
                >
                  <X size={10} weight="bold" />
                </button>
              )}
            </span>
          )}

          {projectId && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-800">
              <Wall size={11} weight="duotone" />
              Project RENO-{projectId}
            </span>
          )}

          {otherFilterCount > 0 && (
            <span className="text-neutral-secondary-mid rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-xs font-medium">
              +{otherFilterCount} {otherFilterCount === 1 ? "filter" : "filters"}
            </span>
          )}

          <span className="text-neutral-secondary-mid ml-auto text-[11px]">
            De tabel en "Deze selectie" volgen deze filters — de volledige boekhouding niet.
          </span>
        </div>
      </div>
    );
  },
);
DocumentsScopeBanner.displayName = "DocumentsScopeBanner";
